import { useEffect, useRef, useState } from "react";
import { Link } from "react-router";
import { Grid3x3Icon, PlusIcon, SearchIcon } from "lucide-react";
import { Button } from "@/shared/components/ui/button";
import { Input } from "@/shared/components/ui/input";
import { cn, formatRelativeTime } from "@/shared/lib/utils";
import { matrixItemMatchesSearch } from "@/domains/matrix";
import { SCROLL_FADE_HEIGHT, SCROLL_FADE_TOP } from "../lib/scroll-fade";
import type { MatrixListItemUi } from "@/domains/matrix";
import { formatMatrixAge, summarizeMatrix } from "../lib/summarize-matrix";
import { useMatrixRailCollapsed } from "../lib/matrix-rail-state";
import { CreateMatrixDialog } from "./create-matrix-dialog";
import { MatrixActionsMenu } from "./matrix-actions-menu";
import type { MatrixActionsMenuProps } from "./matrix-actions-menu";
import { MatrixRowActionDialogs } from "./matrix-row-action-dialogs";
import { MatrixPreview } from "./matrix-preview";
import { useMatrixRowActions } from "../hooks/use-matrix-row-actions";

const RAIL_INSET = 24;

type MatrixSidebarProps = {
  matrices: MatrixListItemUi[];
  activeCollectionId: string | null;
};

export function MatrixSidebar({
  matrices,
  activeCollectionId,
}: MatrixSidebarProps) {
  const actions = useMatrixRowActions(matrices);
  const [search, setSearch] = useState("");
  const listRef = useRef<HTMLElement>(null);
  const topFadeRef = useRef<HTMLDivElement>(null);

  /**
   * Reserves the scrollbar's width out of the list's own inset, so the cards
   * end where the row above them ends whether or not a bar is drawn, and keeps
   * the top fade clear of it.
   */
  function syncScrollChrome() {
    const list = listRef.current;
    const top = topFadeRef.current;
    if (!list || !top) return;

    const gutter = list.offsetWidth - list.clientWidth;
    list.style.paddingRight = `${Math.max(RAIL_INSET - gutter, 0)}px`;
    top.style.right = `${gutter}px`;
    top.hidden = list.scrollTop === 0;
  }

  useEffect(syncScrollChrome);

  useEffect(() => {
    const list = listRef.current;
    if (!list) return;
    const observer = new ResizeObserver(syncScrollChrome);
    observer.observe(list);
    return () => observer.disconnect();
  }, []);

  const visibleMatrices = matrices.filter((matrix) =>
    matrixItemMatchesSearch(matrix, search),
  );
  const [isCollapsed] = useMatrixRailCollapsed();

  if (isCollapsed) return null;

  return (
    <aside
      className={cn(
        "border-border hidden shrink-0 flex-col lg:flex",
        "w-[220px] border-r pt-4 pl-2 sm:pt-6 xl:w-[240px]",
      )}
    >
      <div className="flex items-center gap-2 pr-6 pb-6">
        <CreateMatrixDialog
          trigger={
            <Button
              size="icon"
              aria-label="New matrix"
              title="New matrix"
              className="shrink-0"
            >
              <PlusIcon className="size-4" />
            </Button>
          }
        />
        <div className="relative min-w-0 flex-1">
          <SearchIcon className="text-muted-foreground pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2" />
          <Input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search..."
            aria-label="Search matrices"
            className="pl-9"
          />
        </div>
      </div>
      <div className="relative -ml-1 min-h-0 flex-1">
        <nav
          ref={listRef}
          onScroll={syncScrollChrome}
          style={{ scrollbarGutter: "stable" }}
          className="flex h-full flex-col gap-3 overflow-y-auto pt-1 pb-6 pl-1 [scrollbar-width:thin]"
          aria-label="Project matrices"
        >
          {visibleMatrices.map((matrix) => (
            <MatrixSidebarRow
              key={matrix.collectionId}
              matrix={matrix}
              isActive={matrix.collectionId === activeCollectionId}
              menuProps={actions.menuPropsFor(matrix)}
            />
          ))}
          {matrices.length > 0 && visibleMatrices.length === 0 ? (
            <p className="text-muted-foreground px-1 py-2 text-xs">
              No matrix matches "{search}".
            </p>
          ) : null}
        </nav>

        <div
          ref={topFadeRef}
          aria-hidden
          hidden
          className="pointer-events-none absolute top-0 left-0"
          style={{ height: SCROLL_FADE_HEIGHT, background: SCROLL_FADE_TOP }}
        />
      </div>

      <MatrixRowActionDialogs
        {...actions.dialogProps}
        matrices={matrices}
        activeCollectionId={activeCollectionId}
      />
    </aside>
  );
}

type MatrixSidebarRowProps = {
  matrix: MatrixListItemUi;
  isActive: boolean;
  menuProps: MatrixActionsMenuProps;
};

function MatrixSidebarRow({
  matrix,
  isActive,
  menuProps,
}: MatrixSidebarRowProps) {
  const summary = summarizeMatrix(matrix);
  const updated = `updated ${formatRelativeTime(matrix.updatedAt)}`;

  return (
    <div
      className={cn(
        "group bg-card hover:ring-primary/20 relative w-full min-w-0 rounded-xl border text-sm shadow-sm transition-shadow hover:shadow-md hover:ring-1",
        isActive
          ? "border-primary/20 bg-primary/5 text-primary font-medium"
          : "text-foreground/80",
      )}
    >
      <Link
        to={`/matrix/${matrix.collectionId}`}
        aria-label={[matrix.name, summary, updated].join(", ")}
        title={updated}
        className="focus-visible:ring-ring/50 flex min-w-0 flex-col gap-1.5 rounded-xl px-3 py-2.5 text-left focus-visible:ring-[3px] focus-visible:outline-none"
      >
        <span className="flex min-w-0 items-center gap-2 pr-6">
          <Grid3x3Icon
            className={cn(
              "size-4 shrink-0",
              isActive ? "text-primary" : "text-muted-foreground",
            )}
          />
          <span className="min-w-0 flex-1 truncate">{matrix.name}</span>
        </span>

        <MatrixPreview
          columns={matrix.columns}
          rows={matrix.previewRows}
          rowCount={matrix.rowCount}
          isActive={isActive}
          className="-mx-1"
        />

        <span className="text-muted-foreground flex items-baseline justify-between gap-2 text-[11px] font-normal">
          <span className="truncate">{summary}</span>
          <span className="shrink-0 tabular-nums">
            {formatMatrixAge(matrix.updatedAt)}
          </span>
        </span>
      </Link>
      <MatrixActionsMenu
        {...menuProps}
        align="end"
        triggerClassName="opacity-60 group-hovered:opacity-100 absolute top-2 right-2"
      />
    </div>
  );
}
